import type { MessageApi } from 'naive-ui'
import type { useAgentStore } from '../stores/agent'
import type { useAgentSessionSlotsStore } from '../stores/agent_session_slots'

// Session-row action handlers for AgentView — the glue between the stores
// and user-facing toasts, extracted to keep the view under the file-size
// limit. Stop gets bespoke feedback (#1381/#1380): its teardown is ~10s of
// synchronous `devcontainer down`, so success deserves a toast, and the raw
// proxied error string ("Agent box returned …") is never actionable in a
// toast — keep it in the console instead.
export function useAgentSessionActions(
  store: ReturnType<typeof useAgentStore>,
  slotUsageStore: ReturnType<typeof useAgentSessionSlotsStore>,
  message: MessageApi,
) {
  function notifyError(err: unknown, fallback: string) {
    message.error(err instanceof Error ? err.message : fallback)
  }

  function renameSession(threadId: string, name: string) {
    store.renameSession(threadId, name).catch((err) => notifyError(err, 'Failed to rename session'))
  }

  function deleteSession(threadId: string) {
    store.deleteSession(threadId).catch((err) => notifyError(err, 'Failed to delete session'))
  }

  function stopSession(threadId: string) {
    store
      .stopSession(threadId)
      .then(() => {
        // Stopping frees the session's devcontainer slot — refresh the count.
        slotUsageStore.fetchUsage().catch(() => {})
        message.success('Session stopped')
      })
      .catch((err) => {
        console.error('stopSession failed', err)
        message.error(
          "Couldn't stop the session — the agent box didn't respond. It may still stop; check again shortly.",
        )
      })
  }

  return { notifyError, renameSession, deleteSession, stopSession }
}
